/**
 * AdditionalQuestions Container Component
 *
 * Manages custom questions (additional fields) that can be added to any survey type.
 * This is the "Questions" tab content in survey creation/editing.
 *
 * ## Used By
 * - CustomSurvey - Full custom field support with contact field mapping
 * - EventRegistrationSurvey - Custom questions for attendee information
 * - SatisfactionSurvey - Custom questions with conditional logic (based on rating)
 * - DonationSurvey - Custom questions for donor information
 *
 * ## Features by Survey Type
 *
 * ### All Survey Types
 * - Add/remove/reorder custom questions
 * - Question types: Text input, Dropdown/multiple choice
 * - Mark questions as required/optional
 * - Real-time validation
 * - Drag-and-drop reordering
 *
 * ### Custom Survey Specific
 * - Map questions to contact custom fields (sync responses to CRM)
 * - Tag generation from option fields (e.g., dropdown creates contact tags)
 * - Tag prefix configuration
 * - 2-way sync: survey → contact fields
 *
 * ### Satisfaction Survey Specific
 * - Conditional questions based on rating (e.g., "Why low rating?" only for ratings 1-3)
 * - 20 question limit
 * - Metadata tracking for conditional logic
 *
 * ### Event Registration Survey Specific
 * - Questions for attendee information
 * - Optional by default
 *
 * ## How It Works
 * 1. Detects survey type from form data (has customFormMetaData → Satisfaction, etc.)
 * 2. Loads survey-type-specific configuration
 * 3. Renders FieldList with appropriate features enabled/disabled
 * 4. Manages form state through React Hook Form
 * 5. Syncs metadata for conditional questions (Satisfaction only)
 *
 * ## Required Setup
 * Must be used inside FormProvider with form containing `customForm.fields` array:
 * ```tsx
 * <FormProvider {...methods}>
 *   <form>
 *     <AdditionalQuestions isEdit={true} />
 *   </form>
 * </FormProvider>
 * ```
 *
 * ## Architecture
 * - **Container Component**: This file - orchestrates logic, manages state
 * - **FieldList**: Renders list of questions with add/reorder buttons
 * - **FormFieldWithOptions**: Individual question editor
 * - **useFieldStateManager**: Tracks dirty state for unsaved changes
 * - **useCustomFormMetadata**: Syncs conditional question metadata (Satisfaction)
 *
 * @example Basic usage in CustomSurvey
 * ```tsx
 * <TabContent value={TabValues.Questions}>
 *   <AdditionalQuestions isEdit={isEdit} isClone={isClone} />
 * </TabContent>
 * ```
 */

import { IImpersonationContextComponentProps, ImpersonationContextKey } from '@Models/index';
import { FormFieldType, ICustomFormFieldTagSettings, IFormField } from '@ViewModels';
import { inject } from 'mobx-react';
import * as React from 'react';
import { Controller, FormState, useFormContext } from 'react-hook-form';
import { useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { useContactCustomFields } from '../../../../../../queries/Contact/useContactCustomFields';
import { ISelectBoxOption } from '../../../../../components/SelectBox';

import { FieldList } from './components/FieldList';
import { useCustomFormMetadata } from './hooks/useCustomFormMetadata';
import { useFieldStateManager } from './hooks/useFieldStateManager';
import { AdditionalQuestionsProps, ConditionalVisibilityState, SurveyFormData, SurveyTypeConfig } from './types';
import { buildSurveyForm, hydrateConditionalVisibilityMap } from './utils/surveyFormUtils';

/**
 * Detects survey type and returns appropriate configuration
 *
 * Uses form data structure to identify survey type:
 * - Has `customFormMetaData` → SatisfactionSurvey
 * - Has `customForm` + `options` → CustomSurvey
 * - Otherwise → EventRegistrationSurvey (default)
 *
 * Returns configuration object controlling:
 * - Tag generation settings (prefix support, enabled features)
 * - Field management (removal allowed, default optional state, question limits)
 * - Custom field mapping (contact field sync for CustomSurvey)
 * - Conditional questions (rating-based display for SatisfactionSurvey)
 *
 * @param formValues - Current form state from React Hook Form
 * @returns Survey-specific configuration object
 */
const getSurveyTypeConfig = (formValues: SurveyFormData): SurveyTypeConfig => {
	// Check for SatisfactionSurvey (has customFormMetaData for conditional questions)
	if ('customFormMetaData' in formValues) {
		return {
			tagGeneration: {
				enabled: true,
				supportsTagPrefixes: true,
			},
			fieldManagement: {
				allowRemoval: true,
				defaultOptional: true,
				questionLimit: 20, // SatisfactionSurvey has 20-question limit
			},
			customFieldManagement: {
				enabled: false, // Custom fields only for Custom Surveys
				supportedFieldTypes: [
					FormFieldType.String,
					FormFieldType.Option,
					FormFieldType.MultipleOptions,
					FormFieldType.FileUpload,
				],
			},
			conditionalQuestions: {
				enabled: true,
				metadataField: 'customFormMetaData',
				ratingRange: [1, 5],
				defaultRatings: [1, 2, 3],
			},
			surveyType: 'satisfaction',
		};
	}

	// Check for CustomSurvey (has customForm with fields)
	if ('customForm' in formValues && 'options' in formValues) {
		return {
			tagGeneration: {
				enabled: true,
				supportsTagPrefixes: true,
			},
			fieldManagement: {
				allowRemoval: true,
				defaultOptional: false, // Make all new questions required by default for CustomSurvey
			},
			customFieldManagement: {
				enabled: true, // Custom fields enabled for Custom Surveys
				supportedFieldTypes: [
					FormFieldType.String,
					FormFieldType.Option,
					FormFieldType.MultipleOptions,
					FormFieldType.FileUpload,
				], // Only String and Option types
			},
			surveyType: 'custom',
		};
	}

	// Default to EventRegistrationSurvey
	return {
		tagGeneration: {
			enabled: true,
			supportsTagPrefixes: true,
		},
		fieldManagement: {
			allowRemoval: true,
			defaultOptional: true,
		},
		customFieldManagement: {
			enabled: false, // Custom fields only for Custom Surveys
			supportedFieldTypes: [
				FormFieldType.String,
				FormFieldType.Option,
				FormFieldType.MultipleOptions,
				FormFieldType.FileUpload,
			],
		},
		surveyType: 'eventRegistration',
	};
};

/**
 * AdditionalQuestions container component implementation
 *
 * Responsibilities:
 * 1. Auto-detect survey type from form structure
 * 2. Load appropriate configuration (limits, features, defaults)
 * 3. Manage custom question CRUD operations (add, update, remove, reorder)
 * 4. Sync conditional question metadata (Satisfaction surveys)
 * 5. Handle custom field mapping (Custom surveys)
 * 6. Track dirty state for unsaved changes detection
 *
 * @param isEdit - Whether editing existing survey (vs creating new)
 * @param isClone - Whether cloning existing survey
 * @param impersonationContext - Admin impersonation context for fetching custom fields
 */
const AdditionalQuestionsBase: React.FC<IImpersonationContextComponentProps & AdditionalQuestionsProps> = ({
	isEdit,
	isClone = false,
	templateMode,
	impersonationContext,
	clearedVisibilityFieldIds,
	reorderDirtyFieldIds,
}) => {
	// React Hook Form context - provides form state and setters
	const { watch, control, formState, setValue, trigger, clearErrors } = useFormContext<SurveyFormData>();
	const formValues = watch(); // Watch all form values for reactivity
	const userSession = useUserSession();

	// Auto-detect survey type and load configuration
	// Recalculates when form values change (e.g., when loading saved survey)
	const config = React.useMemo(() => getSurveyTypeConfig(formValues), [formValues]);

	// Extract current custom questions from form state
	// Defaults to empty array if customForm.fields doesn't exist
	const currentFields = React.useMemo((): IFormField<string>[] => {
		return ('customForm' in formValues && formValues.customForm?.fields) || [];
	}, [formValues]);

	// Check if account has custom contact fields feature enabled
	// Only relevant for Custom Surveys which can map questions to contact fields
	const hasCustomContactFields = userSession?.account?.additionalInfo?.hasCustomContactFields;

	// Fetch available contact custom fields (for Custom Surveys only)
	// Only runs query when: Custom Survey + account has feature enabled
	const customFieldsQuery = useContactCustomFields({
		impersonationContext,
		refetchOnWindowFocus: false,
		enabled: config.customFieldManagement.enabled && hasCustomContactFields,
	});

	const availableCustomFields = customFieldsQuery.data?.fields || [];

	// Initialize field state manager - tracks which fields are dirty (modified)
	// Used for unsaved changes detection
	const fieldStateManager = useFieldStateManager(currentFields, isClone);

	// Initialize metadata manager for conditional questions (Satisfaction surveys only)
	// Syncs customFormMetaData array with customForm.fields changes
	const metadataManager = useCustomFormMetadata(config.conditionalQuestions, setValue, watch, currentFields);

	// Extract surveyForm from form values (only present on Custom Surveys)
	const surveyFormData = 'surveyForm' in formValues ? formValues.surveyForm : undefined;

	// Conditional visibility state map for Custom Surveys
	// Maps fieldId → { enabled, dependsOnFieldId, dependsOnOptionIds }
	// Initialized from surveyForm data when editing an existing survey
	const [conditionalVisibilityMap, setConditionalVisibilityMap] = React.useState<
		Map<string, ConditionalVisibilityState>
	>(new Map());

	// Hydrate conditional visibility map from surveyForm when loading an existing survey
	const hasHydratedRef = React.useRef(false);
	React.useEffect(() => {
		if (config.surveyType !== 'custom' || hasHydratedRef.current) return;

		if (surveyFormData?.surveyParts?.length) {
			const hydrated = hydrateConditionalVisibilityMap(surveyFormData);
			// Only mark hydration complete once we have entries to hydrate. The sync
			// effect below can race ahead and write a stripped surveyForm into RHF
			// before useCustomSurveyFormInitialization sets the API value, which
			// would otherwise cause this effect to "hydrate" empty data and lock out
			// the real API value when it arrives.
			if (hydrated.size > 0) {
				setConditionalVisibilityMap(hydrated);
				hasHydratedRef.current = true;
			}
		}
	}, [surveyFormData, config.surveyType]);

	// Clear conditional visibility entries when reordering breaks dependencies
	React.useEffect(() => {
		if (!clearedVisibilityFieldIds?.length) return;

		setConditionalVisibilityMap(prev => {
			const next = new Map(prev);
			let changed = false;
			for (const fieldId of clearedVisibilityFieldIds) {
				if (next.has(fieldId)) {
					next.set(fieldId, { enabled: false });
					changed = true;
				}
			}
			return changed ? next : prev;
		});
	}, [clearedVisibilityFieldIds]);

	// Mark fields dirty when reordering changes their position or clears their visibility
	React.useEffect(() => {
		if (!reorderDirtyFieldIds?.length) return;

		for (const fieldId of reorderDirtyFieldIds) {
			fieldStateManager.markFieldDirty(fieldId);
		}
	}, [reorderDirtyFieldIds, fieldStateManager]);

	// Clean up conditional visibility entries that reference stale fields, types, or options.
	// Called from handlers that modify fields (remove, type change, option update)
	// instead of running as an effect on every currentFields change.
	const cleanupConditionalVisibility = React.useCallback(
		(fields: IFormField<string>[]) => {
			if (config.surveyType !== 'custom') return;

			setConditionalVisibilityMap(prev => {
				let changed = false;
				const next = new Map(prev);

				for (const [fieldId, state] of next) {
					// Check if this field still exists
					const fieldExists = fields.some(f => f.id === fieldId);
					if (!fieldExists) {
						next.delete(fieldId);
						changed = true;
						continue;
					}

					if (!state.dependsOnFieldId) continue;

					const depField = fields.find(f => f.id === state.dependsOnFieldId);

					// Referenced question no longer exists or is no longer Dropdown/Checkbox
					if (
						!depField ||
						(depField.fieldType !== FormFieldType.Option && depField.fieldType !== FormFieldType.MultipleOptions)
					) {
						next.set(fieldId, { enabled: false });
						changed = true;
						continue;
					}

					// Referenced question's options changed — remove any answer IDs that no longer exist
					if (state.dependsOnOptionIds?.length) {
						const validIds = state.dependsOnOptionIds.filter(optId =>
							depField.options?.some(opt => opt.id === optId && opt.label?.trim())
						);
						if (validIds.length !== state.dependsOnOptionIds.length) {
							next.set(fieldId, { ...state, dependsOnOptionIds: validIds.length > 0 ? validIds : undefined });
							changed = true;
						}
					}
				}

				return changed ? next : prev;
			});
		},
		[config.surveyType]
	);

	const handleConditionalVisibilityToggle = React.useCallback(
		(fieldId: string, enabled: boolean) => {
			userVisibilityChangeRef.current = true;
			setConditionalVisibilityMap(prev => {
				const next = new Map(prev);
				if (enabled) {
					next.set(fieldId, { enabled: true });
				} else {
					next.set(fieldId, { enabled: false });
				}
				return next;
			});
			fieldStateManager.markFieldDirty(fieldId);
		},
		[fieldStateManager]
	);

	const handleConditionalVisibilityQuestionSelect = React.useCallback(
		(fieldId: string, dependsOnFieldId: string) => {
			userVisibilityChangeRef.current = true;
			setConditionalVisibilityMap(prev => {
				const next = new Map(prev);
				next.set(fieldId, { enabled: true, dependsOnFieldId, dependsOnOptionIds: undefined });
				return next;
			});
			fieldStateManager.markFieldDirty(fieldId);
		},
		[fieldStateManager]
	);

	const handleConditionalVisibilityAnswerSelect = React.useCallback(
		(fieldId: string, answerIndex: number, optionId: string) => {
			userVisibilityChangeRef.current = true;
			setConditionalVisibilityMap(prev => {
				const next = new Map(prev);
				const current = next.get(fieldId);
				if (current) {
					const ids = [...(current.dependsOnOptionIds || [])];
					ids[answerIndex] = optionId;
					next.set(fieldId, { ...current, dependsOnOptionIds: ids });
				}
				return next;
			});
			fieldStateManager.markFieldDirty(fieldId);
		},
		[fieldStateManager]
	);

	const handleConditionalVisibilityAnswerAdd = React.useCallback(
		(fieldId: string) => {
			userVisibilityChangeRef.current = true;
			setConditionalVisibilityMap(prev => {
				const next = new Map(prev);
				const current = next.get(fieldId);
				if (current) {
					const ids = [...(current.dependsOnOptionIds || [])];
					ids.push(''); // Add empty placeholder for new answer row
					next.set(fieldId, { ...current, dependsOnOptionIds: ids });
				}
				return next;
			});
			fieldStateManager.markFieldDirty(fieldId);
		},
		[fieldStateManager]
	);

	const handleConditionalVisibilityAnswerRemove = React.useCallback(
		(fieldId: string, answerIndex: number) => {
			userVisibilityChangeRef.current = true;
			setConditionalVisibilityMap(prev => {
				const next = new Map(prev);
				const current = next.get(fieldId);
				if (current?.dependsOnOptionIds) {
					const ids = current.dependsOnOptionIds.filter((_, i) => i !== answerIndex);
					next.set(fieldId, { ...current, dependsOnOptionIds: ids.length > 0 ? ids : undefined });
				}
				return next;
			});
			fieldStateManager.markFieldDirty(fieldId);
		},
		[fieldStateManager]
	);

	// Stable key representing the field structure — only changes when fields are
	// added, removed, reordered, or change type (not on label/option text edits).
	// buildSurveyForm and validation entries only depend on field IDs and types.
	const fieldStructureKey = React.useMemo(
		() => currentFields.map(f => `${f.id}:${f.fieldType}`).join(','),
		[currentFields]
	);

	// Keep a ref to currentFields so the effect can read the latest array
	// without needing the full (frequently-changing) object as a dependency.
	const currentFieldsRef = React.useRef(currentFields);
	currentFieldsRef.current = currentFields;

	// Tracks whether the next sync of `surveyForm` was triggered by a user
	// dependency edit (vs. initial mount, hydration, or field-structure cleanup).
	// Without this, the sync effect would call setValue on every render and
	// either always mark the form dirty (false-positive banner on load) or
	// never mark it dirty (the bug this guards against — see useUnsavedChanges).
	const userVisibilityChangeRef = React.useRef(false);

	// Sync surveyForm and visibility validation into react-hook-form state for Custom Surveys.
	// Uses fieldStructureKey instead of currentFields to avoid re-running on every keystroke.
	React.useEffect(() => {
		if (config.surveyType !== 'custom') return;
		const fields = currentFieldsRef.current;
		const surveyForm = buildSurveyForm(fields, conditionalVisibilityMap);
		const isUserChange = userVisibilityChangeRef.current;
		const setValueOpts = isUserChange ? { shouldDirty: true } : undefined;
		setValue('surveyForm', surveyForm, setValueOpts);

		// Build per-field validation entries for yup schema
		const validationEntries = fields.map(field => {
			const state = conditionalVisibilityMap.get(field.id);
			if (!state?.enabled) {
				return { enabled: false, hasQuestion: false, hasAllAnswers: false };
			}
			const hasQuestion = !!state.dependsOnFieldId;
			const optionIds = state.dependsOnOptionIds ?? [];
			const hasAllAnswers = optionIds.length > 0 && optionIds.every(id => id && id.trim() !== '');
			return { enabled: true, hasQuestion, hasAllAnswers };
		});
		setValue('surveyFormVisibilityValidation', validationEntries, setValueOpts);
		userVisibilityChangeRef.current = false;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fieldStructureKey, conditionalVisibilityMap, config.surveyType, setValue]);

	// Memoize tag generation settings to avoid unnecessary re-renders
	// Used for tag creation from option fields (CustomSurvey)
	const tagGenerationSettings = React.useMemo(
		() => formValues.tagGenerationSettings,
		[formValues.tagGenerationSettings]
	);

	// Memoize custom field mapping (which survey questions map to which contact fields)
	// Only exists for Custom Surveys
	const customFieldMap = React.useMemo(() => {
		// Type-safe access - only CustomSurvey has customFieldMap property
		if ('customFieldMap' in formValues) {
			return formValues.customFieldMap;
		}
		return undefined;
	}, [formValues]);

	/**
	 * Adds a new custom question to the form
	 *
	 * Creates a new field with default values based on survey type configuration:
	 * - ID: Random UUID for tracking
	 * - Type: String (text input) by default
	 * - Label: Empty (user fills in)
	 * - Optional: Survey-specific default (CustomSurvey=required, others=optional)
	 *
	 * The new field is appended to the end of the fields array.
	 */
	const handleFieldAdd = React.useCallback(() => {
		const newField: IFormField<string> = {
			fieldType: FormFieldType.String, // Default to text input
			id: crypto.randomUUID(), // Generate unique ID for tracking
			label: '', // Empty label - user will fill in
			isHidden: false, // Visible by default
			isOptional: config.fieldManagement.defaultOptional, // Survey-specific default
		};
		// Append to existing fields
		setValue('customForm.fields', [...currentFields, newField]);
	}, [currentFields, setValue, config.fieldManagement.defaultOptional]);

	/**
	 * Updates an existing custom question
	 *
	 * Called when user edits question label, type, options, or required/optional status.
	 * Also marks field as "dirty" for unsaved changes detection.
	 *
	 * @param field - Updated field object
	 * @param index - Position in fields array
	 */
	const handleFieldUpdate = React.useCallback(
		(field: IFormField<string>, index: number) => {
			const newFields = [...currentFields];
			newFields[index] = field;
			setValue('customForm.fields', newFields);
			// Mark as dirty for unsaved changes warning
			fieldStateManager.markFieldDirtyIfChanged(field);
			// Clean up visibility entries referencing removed options
			cleanupConditionalVisibility(newFields);
		},
		[currentFields, setValue, fieldStateManager, cleanupConditionalVisibility]
	);

	/**
	 * Cleans up tag generation settings for a removed field
	 */
	const cleanupTagGenerationSettings = React.useCallback(
		(fieldId: string) => {
			if (!config.tagGeneration.enabled) return;

			const currentTagSettings = tagGenerationSettings || {
				tagOnCompletion: false,
				completionTag: '',
				customFormFieldTagSettings: [],
			};

			const newTagSettings = {
				...currentTagSettings,
				customFormFieldTagSettings: currentTagSettings.customFormFieldTagSettings.filter(
					(setting: ICustomFormFieldTagSettings) => setting.fieldId !== fieldId
				),
			};

			setValue('tagGenerationSettings', newTagSettings);
		},
		[config.tagGeneration.enabled, tagGenerationSettings, setValue]
	);

	/**
	 * Cleans up custom field mappings for a removed field
	 */
	const cleanupCustomFieldMappings = React.useCallback(
		(fieldId: string) => {
			if (!config.customFieldManagement.enabled) return;

			const currentCustomFieldMap = customFieldMap || [];
			const newCustomFieldMap = currentCustomFieldMap.filter(
				(mapping: { fieldId: string }) => mapping.fieldId !== fieldId
			);

			setValue('customFieldMap', newCustomFieldMap);
		},
		[config.customFieldManagement.enabled, customFieldMap, setValue]
	);

	/**
	 * Removes a field from the survey and cleans up associated settings
	 */
	const handleFieldRemove = React.useCallback(
		(index: number) => {
			const fieldToRemove = currentFields[index];
			const removedFieldId = fieldToRemove.id;

			// Remove the field from the custom form
			const newFields = [...currentFields];
			newFields.splice(index, 1);
			setValue('customForm.fields', newFields);

			// Clean up associated settings
			cleanupTagGenerationSettings(removedFieldId);
			cleanupCustomFieldMappings(removedFieldId);
			// Clean up visibility entries referencing the removed field
			cleanupConditionalVisibility(newFields);
		},
		[currentFields, setValue, cleanupTagGenerationSettings, cleanupCustomFieldMappings, cleanupConditionalVisibility]
	);

	const handleLabelChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>, index: number) => {
			const newFields = [...currentFields];
			newFields[index].label = e.target.value;
			setValue('customForm.fields', newFields);
			fieldStateManager.markFieldDirtyIfChanged(newFields[index]);

			// Clear validation error if label now has content and field has options
			const currentField = newFields[index];
			const hasLabel = currentField.label?.trim();
			const hasOptions = currentField.options?.length && currentField.options.some(option => option.label?.trim());

			if (hasLabel && hasOptions) {
				// Clear both label and options errors
				clearErrors(`customForm.fields.${index}.label`);
				clearErrors(`customForm.fields.${index}.options`);
			}
		},
		[currentFields, setValue, fieldStateManager, clearErrors]
	);

	const handleToggleOptional = React.useCallback(
		(index: number) => {
			const newFields = [...currentFields];
			newFields[index].isOptional = !newFields[index].isOptional;
			setValue('customForm.fields', newFields);
			fieldStateManager.markFieldDirtyIfChanged(newFields[index]);
		},
		[currentFields, setValue, fieldStateManager]
	);

	const handleFieldTypeChange = React.useCallback(
		(selectedType: ISelectBoxOption<FormFieldType>, questionIndex: number) => {
			const newFields = [...currentFields];
			newFields[questionIndex].fieldType = selectedType.value!;
			setValue('customForm.fields', newFields);
			fieldStateManager.markFieldDirtyIfChanged(newFields[questionIndex]);
			// Clean up visibility entries if type changed away from Dropdown/Checkbox
			cleanupConditionalVisibility(newFields);
		},
		[currentFields, setValue, fieldStateManager, cleanupConditionalVisibility]
	);

	// Tag management handlers (only for surveys that support tags)
	const handleTagPrefixChange = React.useCallback(
		(fieldId: string, tagPrefix: string) => {
			if (!config.tagGeneration.enabled) return;

			const currentTagSettings = tagGenerationSettings || {
				tagOnCompletion: false,
				completionTag: '',
				customFormFieldTagSettings: [],
			};

			const existingIndex = currentTagSettings.customFormFieldTagSettings.findIndex(
				(setting: ICustomFormFieldTagSettings) => setting.fieldId === fieldId
			);

			const newTagSettings = {
				...currentTagSettings,
				customFormFieldTagSettings: [...currentTagSettings.customFormFieldTagSettings],
			};

			if (existingIndex >= 0) {
				newTagSettings.customFormFieldTagSettings[existingIndex] = {
					fieldId,
					shouldGenerateTag: true,
					tagPrefix,
				};
			} else {
				newTagSettings.customFormFieldTagSettings.push({
					fieldId,
					shouldGenerateTag: true,
					tagPrefix,
				});
			}

			setValue('tagGenerationSettings', newTagSettings);

			// Mutual exclusivity: Remove any existing custom field for this field
			if (config.customFieldManagement.enabled) {
				const currentCustomFieldMap = customFieldMap || [];

				const newCustomFieldMap = currentCustomFieldMap.filter(
					(mapping: { fieldId: string }) => mapping.fieldId !== fieldId
				);

				setValue('customFieldMap', newCustomFieldMap);
			}

			fieldStateManager.markFieldDirty(fieldId);
		},
		[
			config.tagGeneration.enabled,
			config.customFieldManagement.enabled,
			tagGenerationSettings,
			customFieldMap,
			setValue,
			fieldStateManager,
		]
	);

	const handleTagPrefixDelete = React.useCallback(
		(fieldId: string) => {
			if (!config.tagGeneration.enabled) return;

			const currentTagSettings = tagGenerationSettings || {
				tagOnCompletion: false,
				completionTag: '',
				customFormFieldTagSettings: [],
			};

			const newTagSettings = {
				...currentTagSettings,
				customFormFieldTagSettings: currentTagSettings.customFormFieldTagSettings.filter(
					(setting: ICustomFormFieldTagSettings) => setting.fieldId !== fieldId
				),
			};

			setValue('tagGenerationSettings', newTagSettings);
		},
		[config.tagGeneration.enabled, tagGenerationSettings, setValue]
	);

	// Custom field handlers (only for Custom Surveys)
	const handleCustomFieldChange = React.useCallback(
		(fieldId: string, _customFieldId: string, customFieldName: string) => {
			if (!config.customFieldManagement.enabled) return;

			const currentCustomFieldMap = customFieldMap || [];

			const existingIndex = currentCustomFieldMap.findIndex(
				(mapping: { fieldId: string }) => mapping.fieldId === fieldId
			);

			const newCustomFieldMap = [...currentCustomFieldMap];

			if (existingIndex >= 0) {
				newCustomFieldMap[existingIndex] = {
					fieldId,
					contactFieldId: customFieldName, // Backend expects the field name
				};
			} else {
				newCustomFieldMap.push({
					fieldId,
					contactFieldId: customFieldName, // Backend expects the field name
				});
			}

			setValue('customFieldMap', newCustomFieldMap);

			// Mutual exclusivity: Remove any existing tag prefix for this field
			if (config.tagGeneration.enabled) {
				const currentTagSettings = tagGenerationSettings || {
					tagOnCompletion: false,
					completionTag: '',
					customFormFieldTagSettings: [],
				};

				const newTagSettings = {
					...currentTagSettings,
					customFormFieldTagSettings: currentTagSettings.customFormFieldTagSettings.filter(
						(setting: ICustomFormFieldTagSettings) => setting.fieldId !== fieldId
					),
				};

				setValue('tagGenerationSettings', newTagSettings);
			}

			fieldStateManager.markFieldDirty(fieldId);
		},
		[
			config.customFieldManagement.enabled,
			config.tagGeneration.enabled,
			customFieldMap,
			tagGenerationSettings,
			setValue,
			fieldStateManager,
		]
	);

	const handleCustomFieldDelete = React.useCallback(
		(fieldId: string) => {
			if (!config.customFieldManagement.enabled) return;

			const currentCustomFieldMap = customFieldMap || [];

			const newCustomFieldMap = currentCustomFieldMap.filter(
				(mapping: { fieldId: string }) => mapping.fieldId !== fieldId
			);

			setValue('customFieldMap', newCustomFieldMap);
			fieldStateManager.markFieldDirty(fieldId);
		},
		[config.customFieldManagement.enabled, customFieldMap, setValue, fieldStateManager]
	);

	// Conditional questions handlers (only for SatisfactionSurvey)
	const handleConditionalToggle = React.useCallback(
		(fieldId: string, isConditional: boolean) => {
			if (!config.conditionalQuestions?.enabled) return;
			metadataManager.handleConditionalToggle(fieldId, isConditional);
			fieldStateManager.markFieldDirty(fieldId);
		},
		[config.conditionalQuestions?.enabled, metadataManager, fieldStateManager]
	);

	const handleRatingThresholdChange = React.useCallback(
		(fieldId: string, threshold: number) => {
			if (!config.conditionalQuestions?.enabled) return;
			metadataManager.handleRatingThresholdChange(fieldId, threshold);
			fieldStateManager.markFieldDirty(fieldId);
		},
		[config.conditionalQuestions?.enabled, metadataManager, fieldStateManager]
	);

	// Validation trigger function for tag creation
	const handleValidationTrigger = React.useCallback(
		async (questionIndex: number) => {
			const fieldPath = `customForm.fields.${questionIndex}` as const;

			const isValid = await trigger(fieldPath);

			// The trigger function will automatically set the error on the correct path
			// based on the validation schema, so we don't need to manually set it
			return isValid;
		},
		[trigger]
	);

	return (
		<Controller
			name='customForm.fields'
			control={control}
			render={() => (
				<FieldList
					fields={currentFields}
					isEdit={isEdit}
					isClone={isClone}
					config={config}
					formState={formState as FormState<Record<string, unknown>>}
					tagGenerationSettings={tagGenerationSettings}
					customFieldMap={customFieldMap}
					availableCustomFields={availableCustomFields}
					onFieldAdd={handleFieldAdd}
					onFieldUpdate={handleFieldUpdate}
					onFieldRemove={handleFieldRemove}
					onLabelChange={handleLabelChange}
					onToggleOptional={handleToggleOptional}
					onFieldTypeChange={handleFieldTypeChange}
					onTagPrefixChange={config.tagGeneration.enabled ? handleTagPrefixChange : undefined}
					onTagPrefixDelete={config.tagGeneration.enabled ? handleTagPrefixDelete : undefined}
					onCustomFieldChange={config.customFieldManagement.enabled ? handleCustomFieldChange : undefined}
					onCustomFieldDelete={config.customFieldManagement.enabled ? handleCustomFieldDelete : undefined}
					onConditionalToggle={config.conditionalQuestions?.enabled ? handleConditionalToggle : undefined}
					onRatingThresholdChange={config.conditionalQuestions?.enabled ? handleRatingThresholdChange : undefined}
					getFieldMetadata={config.conditionalQuestions?.enabled ? metadataManager.getFieldMetadata : undefined}
					isFieldDirty={fieldStateManager.isFieldDirty}
					onValidationTrigger={handleValidationTrigger}
					templateMode={templateMode}
					conditionalVisibilityMap={config.surveyType === 'custom' ? conditionalVisibilityMap : undefined}
					onConditionalVisibilityToggle={config.surveyType === 'custom' ? handleConditionalVisibilityToggle : undefined}
					onConditionalVisibilityQuestionSelect={
						config.surveyType === 'custom' ? handleConditionalVisibilityQuestionSelect : undefined
					}
					onConditionalVisibilityAnswerSelect={
						config.surveyType === 'custom' ? handleConditionalVisibilityAnswerSelect : undefined
					}
					onConditionalVisibilityAnswerAdd={
						config.surveyType === 'custom' ? handleConditionalVisibilityAnswerAdd : undefined
					}
					onConditionalVisibilityAnswerRemove={
						config.surveyType === 'custom' ? handleConditionalVisibilityAnswerRemove : undefined
					}
				/>
			)}
		/>
	);
};

export const AdditionalQuestions = inject(ImpersonationContextKey)(AdditionalQuestionsBase);
